/**
 * Point-in-Time Timeline (feat-125, Slice B / Part 2) — admin-only.
 *
 * "At {T} at this location, what was every recording employee saying, what did
 * the AI detect, and what did the customer buy — all on ONE clock?"
 *
 * Generalizes PosInspectorDetail.tsx's single-column `Timeline` to N side-by-side
 * columns + an order column, all sharing ONE vertical restaurant-local axis.
 *
 * Contract: worktrees/feat-125/decisions/0001-pit-timeline-endpoint.md §D2 + §D8.
 * Positioning is the ONLY math the FE does (§D8):
 *     topPx = (offset_seconds / window.duration_seconds) * AXIS_HEIGHT
 * The FE never parses `at_local` for positioning (display only) and NEVER touches
 * `opened_at` / UTC (AC15 grep-guarded). `at_local` time-of-day is shown via a pure
 * string slice — no Date construction, no tz arithmetic.
 *
 * Locked decisions (0000-pl-locked-decisions.md):
 *   OQ1 in-window orders by opened_at; canceled anchor always shown + badged.
 *   OQ2 attribution = metadata + reciprocal hover-highlight (data-link-id); no SVG connector.
 *   OQ3 columns:[] → "No recordings active in this window"; axis + order column still render.
 *   OQ4 many columns → horizontal scroll, render ALL, header states the count.
 */
import React from 'react';
import ScorecardNavigation from '../ScorecardNavigation';
import { money, StateChip, Chip, serverInitials, seriesClasses } from './PosInspectorDetail';
import {
  fetchPitTimeline,
  listAllLocations,
  PitNotFoundError,
  PitValidationError,
  PitTimelineResponse,
  PitColumn,
  PitOrder,
  PitInteraction,
  PitLocationOption,
} from '../../../services/timelinePitService';

// ---------- positioning (the only math — §D8) ----------

const AXIS_HEIGHT = 1440; // px for the full 360s window → 4 px/sec
const COLUMN_WIDTH = 'w-80'; // 320px per server column
const ORDER_COLUMN_WIDTH = 'w-96'; // 384px — wider so receipts (item name + price + attribution) aren't clipped

const topPx = (offsetSeconds: number, durationSeconds: number): number =>
  (offsetSeconds / durationSeconds) * AXIS_HEIGHT;

/** Pure display helper: extract HH:MM:SS from a naive-local ISO string. NOT date parsing. */
const clockOf = (atLocal: string): string => (atLocal.length >= 19 ? atLocal.slice(11, 19) : atLocal.slice(11));

/** signed m:ss relative to T (display only). */
const relToT = (offset: number, tOffset: number): string => {
  const d = Math.round(offset - tOffset);
  const sign = d < 0 ? '−' : d > 0 ? '+' : '';
  const abs = Math.abs(d);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
};

const serverLabel = (c: { server_name: string | null; server_id: string }): string =>
  c.server_name ?? c.server_id ?? 'Unknown';

// ---------- shared hover-pairing context (OQ2) ----------

interface HoverCtx {
  hovered: string | null;
  setHovered: (id: string | null) => void;
}
const Hover = React.createContext<HoverCtx>({ hovered: null, setHovered: () => {} });

// ---------- search panel ----------

const LocationPicker: React.FC<{
  value: string;
  onChange: (id: string) => void;
}> = ({ value, onChange }) => {
  const [locations, setLocations] = React.useState<PitLocationOption[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let alive = true;
    listAllLocations()
      .then((l) => alive && setLocations(l))
      .catch((e) => alive && setLoadError(e instanceof Error ? e.message : 'Failed to load locations.'));
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = locations?.find((l) => l.id === value) ?? null;
  const q = query.trim().toLowerCase();
  const filtered = (locations ?? []).filter(
    (l) => !q || l.name.toLowerCase().includes(q) || (l.tenant_name ?? '').toLowerCase().includes(q),
  );

  return (
    <div className="relative" ref={ref} data-testid="pit-location-combobox">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full border border-softBorder rounded px-2 py-1.5 text-left flex items-center justify-between gap-2"
        data-testid="pit-location-trigger"
      >
        <span className={`truncate ${selected ? 'text-ink' : 'text-inkMuted'}`}>
          {selected ? selected.name : locations === null && !loadError ? 'Loading locations…' : 'Select a location…'}
        </span>
        <span className="text-inkMuted shrink-0">▾</span>
      </button>
      {loadError && <p className="mt-1 text-xs text-danger-text" data-testid="pit-location-error">{loadError}</p>}
      {open && (
        <div
          className="absolute z-20 mt-1 w-full bg-white border border-softBorder rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col"
          data-testid="pit-location-menu"
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all locations / tenants…"
            className="m-2 px-2 py-1.5 border border-softBorder rounded text-sm shrink-0"
            data-testid="pit-location-search"
          />
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-inkMuted">{locations === null ? 'Loading…' : 'No matching locations.'}</p>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => {
                    onChange(l.id);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-cream/70 ${l.id === value ? 'bg-coral-light/40' : ''}`}
                  data-testid={`pit-location-option-${l.id}`}
                >
                  <div className="text-sm text-ink">{l.name}</div>
                  {l.tenant_name && <div className="text-xs text-inkMuted">{l.tenant_name}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- axis ----------

const TICK_SECONDS = 30;

const TimeAxis: React.FC<{ duration: number; tOffset: number; tLocal: string }> = ({ duration, tOffset, tLocal }) => {
  const ticks: number[] = [];
  for (let s = 0; s <= duration; s += TICK_SECONDS) ticks.push(s);
  return (
    <div className="relative shrink-0 w-20 border-r border-softBorder" style={{ height: AXIS_HEIGHT }} data-testid="pit-axis">
      {ticks.map((s) => {
        const isT = Math.round(s) === Math.round(tOffset);
        return (
          <div key={s} className="absolute right-0 left-0 flex items-center justify-end pr-2" style={{ top: topPx(s, duration) }}>
            <span className={`tabular-nums ${isT ? 'text-coral font-bold text-xs' : 'text-[10px] text-inkMuted'}`}>
              {isT ? `T ${clockOf(tLocal)}` : relToT(s, tOffset)}
            </span>
            <span className={`absolute right-0 w-2 h-px ${isT ? 'bg-coral' : 'bg-softBorder'}`} />
          </div>
        );
      })}
    </div>
  );
};

/** Faint gridlines + the coral T sweep line drawn behind every column for cross-column alignment. */
const ColumnGrid: React.FC<{ duration: number; tOffset: number }> = ({ duration, tOffset }) => {
  const lines: number[] = [];
  for (let s = 0; s <= duration; s += 60) lines.push(s);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {lines.map((s) => (
        <div key={s} className="absolute left-0 right-0 h-px bg-softBorder/40" style={{ top: topPx(s, duration) }} />
      ))}
      <div className="absolute left-0 right-0 h-0.5 bg-coral/50" style={{ top: topPx(tOffset, duration) }} data-testid="pit-t-line" />
    </div>
  );
};

// ---------- server column ----------

const ServerColumn: React.FC<{
  column: PitColumn;
  series: number;
  duration: number;
  tOffset: number;
}> = ({ column, series, duration, tOffset }) => {
  const s = seriesClasses(series);
  const name = serverLabel(column);
  // which transcript part is hovered → lift + un-clamp it above its overlapping neighbors.
  const [hoveredPart, setHoveredPart] = React.useState<string | null>(null);
  // offset of the transcript part a badge is pinned to (else the interaction's own offset).
  const partOffset = new Map(column.transcript_parts.map((p) => [p.id, p.offset_seconds]));
  const badgeOffset = (i: PitInteraction): number =>
    (i.attributed_transcript_part_id != null ? partOffset.get(i.attributed_transcript_part_id) : undefined) ??
    i.offset_seconds;

  return (
    <div className={`${COLUMN_WIDTH} shrink-0 border-r border-softBorder`} data-testid={`pit-column-${column.shift_id}`}>
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-softBorder px-3 py-2 flex items-center gap-2">
        <span className={`w-6 h-6 rounded-full text-[10px] font-bold inline-flex items-center justify-center shrink-0 ${s.avatar}`}>
          {serverInitials(name)}
        </span>
        <span className={`text-sm font-semibold truncate ${s.name}`}>{name}</span>
      </div>
      <div className="relative" style={{ height: AXIS_HEIGHT }}>
        <ColumnGrid duration={duration} tOffset={tOffset} />

        {column.transcript_parts.map((p) => {
          const isCustomer = p.speaker !== 0;
          const open = hoveredPart === p.id;
          const sideBorder = isCustomer ? `border-l-4 ${s.rowBorderSoft}` : `border-l-4 ${s.rowBorder}`;
          const bg = open ? 'bg-white' : isCustomer ? 'bg-cream-dark/40' : '';
          return (
            <div
              key={p.id}
              className={`absolute rounded-r pl-2 pr-1 py-1 text-xs transition-shadow ${sideBorder} ${bg} ${
                open ? 'left-1 right-1 z-30 shadow-lg ring-1 ring-coral/30' : 'left-1 right-20'
              }`}
              style={{ top: topPx(p.offset_seconds, duration) }}
              data-testid={`pit-part-${p.id}`}
              title={clockOf(p.at_local)}
              onMouseEnter={() => setHoveredPart(p.id)}
              onMouseLeave={() => setHoveredPart(null)}
            >
              {open && (
                <div className="flex items-center justify-between gap-2 mb-0.5 text-[10px] text-inkMuted">
                  <span className="font-semibold uppercase tracking-wide">{isCustomer ? 'Customer' : 'Server'}</span>
                  <span className="tabular-nums">{clockOf(p.at_local)}</span>
                </div>
              )}
              <div className={open ? '' : 'line-clamp-2'}>
                <span className="font-mono text-[10px] font-semibold text-inkMuted mr-1">[{p.speaker}]</span>
                <span className={isCustomer ? 'text-inkMuted' : 'text-ink'}>{p.text}</span>
              </div>
            </div>
          );
        })}

        {column.interactions.map((i) => (
          <InteractionBadge key={i.id} interaction={i} duration={duration} top={topPx(badgeOffset(i), duration)} />
        ))}
      </div>
    </div>
  );
};

const isConverted = (i: PitInteraction): boolean =>
  !/^(missed_|no_)/.test(i.interaction_type);

/** Render primitive entries of shift_interaction.data as plain text (React auto-escaping; no HTML injection). */
const renderInteractionData = (data: Record<string, unknown> | null): React.ReactNode => {
  if (!data) return null;
  const entries = Object.entries(data).filter(
    ([, v]) => v != null && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'),
  );
  if (entries.length === 0) return null;
  return (
    <dl className="space-y-0.5 mt-1 border-t border-softBorder/60 pt-1" data-testid="pit-interaction-data">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-1 text-[10px]">
          <dt className="text-inkMuted shrink-0">{k}</dt>
          <dd className="text-ink break-words min-w-0">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
};

const InteractionBadge: React.FC<{ interaction: PitInteraction; duration: number; top: number }> = ({ interaction, top }) => {
  const { hovered, setHovered } = React.useContext(Hover);
  const [open, setOpen] = React.useState(false);
  const linked = hovered === interaction.id;
  const converted = isConverted(interaction);
  const chip = converted
    ? 'bg-success-light text-success border-success/30'
    : 'bg-preview-light text-preview border-preview-border';
  return (
    <div
      className={`absolute right-1 ${open ? 'w-64 z-40' : 'w-[88px]'}`}
      style={{ top: Math.max(0, top - 8) }}
      data-link-id={interaction.id}
      data-testid={`pit-interaction-${interaction.id}`}
      onMouseEnter={() => {
        setHovered(interaction.id);
        setOpen(true);
      }}
      onMouseLeave={() => {
        setHovered(null);
        setOpen(false);
      }}
    >
      {open ? (
        <div
          className={`rounded-lg border bg-white shadow-lg p-2 text-left ${linked ? 'ring-2 ring-coral ring-offset-1 border-coral/40' : 'border-softBorder'}`}
          data-testid={`pit-interaction-detail-${interaction.id}`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border ${chip}`}>
              {interaction.interaction_type}
            </span>
            <span className="text-[10px] tabular-nums text-inkMuted shrink-0">{clockOf(interaction.at_local)}</span>
          </div>
          {interaction.attributed_transcript_part_id != null && (
            <div className="text-[10px] text-inkMuted">
              Anchored to transcript <span className="font-mono text-ink">{interaction.attributed_transcript_part_id}</span>
            </div>
          )}
          {renderInteractionData(interaction.data)}
        </div>
      ) : (
        <span
          className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border text-right leading-tight ${chip} ${
            linked ? 'ring-2 ring-coral ring-offset-1' : ''
          }`}
          title={`${interaction.interaction_type} @ ${clockOf(interaction.at_local)}`}
        >
          {interaction.interaction_type}
        </span>
      )}
    </div>
  );
};

// ---------- order column ----------

interface InteractionMeta {
  server: string;
  atLocal: string;
}

const OrderColumn: React.FC<{
  orders: PitOrder[];
  duration: number;
  tOffset: number;
  interactionMeta: Map<string, InteractionMeta>;
}> = ({ orders, duration, tOffset, interactionMeta }) => (
  <div className={`${ORDER_COLUMN_WIDTH} shrink-0`} data-testid="pit-order-column">
    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-softBorder px-3 py-2">
      <span className="text-sm font-semibold text-ink">🧾 Orders</span>
      <span className="text-xs text-inkMuted ml-2 tabular-nums">{orders.length}</span>
    </div>
    <div className="relative" style={{ height: AXIS_HEIGHT }}>
      <ColumnGrid duration={duration} tOffset={tOffset} />
      {orders.length === 0 && (
        <p className="absolute left-2 right-2 text-xs text-inkMuted italic" style={{ top: topPx(tOffset, duration) + 8 }} data-testid="pit-orders-empty">
          No orders opened in this window.
        </p>
      )}
      {orders.map((o) => (
        <OrderCard key={o.pos_order_id} order={o} top={topPx(o.offset_seconds, duration)} interactionMeta={interactionMeta} />
      ))}
    </div>
  </div>
);

const OrderCard: React.FC<{ order: PitOrder; top: number; interactionMeta: Map<string, InteractionMeta> }> = ({ order, top, interactionMeta }) => {
  // Order-level summary: does ANY line item carry an upsell attribution? (derived, no backend dep.)
  const hasAttribution = order.line_items.some((li) => (li.attribution?.length ?? 0) > 0);
  return (
  <div
    className={`absolute left-1 right-1 rounded-lg border bg-white shadow-sm p-2 text-xs ${
      order.is_anchor ? 'border-coral ring-1 ring-coral/40' : 'border-softBorder'
    }`}
    style={{ top: Math.max(0, top - 8) }}
    data-testid={`pit-order-${order.pos_order_id}`}
  >
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="font-mono text-[10px] text-ink truncate">{order.vendor_order_id}</span>
      <div className="flex items-center gap-1 shrink-0">
        {hasAttribution && (
          <span
            className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-gold-light text-gold border-gold/30"
            data-testid={`pit-order-upsell-${order.pos_order_id}`}
            title="This order has at least one line item attributed to an upsell"
          >
            ↑ upsell
          </span>
        )}
        {order.is_anchor && <Chip tone="coral">anchor</Chip>}
        {order.is_canceled && <Chip tone="warning">canceled</Chip>}
        {!order.is_canceled && <StateChip state={order.state} />}
      </div>
    </div>
    <div className="text-[10px] text-inkMuted mb-1 tabular-nums">
      {clockOf(order.at_local)} · {order.total_money != null ? money(order.total_money, order.currency) : '—'}
    </div>
    <ul className="space-y-1">
      {order.line_items.map((li) => (
        <li key={li.id} className="border-t border-softBorder/60 pt-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-ink min-w-0 break-words">
              {li.quantity != null ? `${li.quantity}× ` : ''}
              {li.name}
            </span>
            <span className="tabular-nums text-inkMuted shrink-0">
              {li.total_money != null ? money(li.total_money, order.currency) : li.unit_price != null ? money(li.unit_price, order.currency) : ''}
            </span>
          </div>
          {li.attribution.map((a) => (
            <AttributionTag key={a.shift_interaction_id} interactionId={a.shift_interaction_id} matchRule={a.match_rule} explanation={a.explanation} meta={interactionMeta.get(a.shift_interaction_id)} />
          ))}
        </li>
      ))}
    </ul>
  </div>
  );
};

const AttributionTag: React.FC<{
  interactionId: string;
  matchRule: string;
  explanation: string | null;
  meta?: InteractionMeta;
}> = ({ interactionId, matchRule, explanation, meta }) => {
  const { hovered, setHovered } = React.useContext(Hover);
  const linked = hovered === interactionId;
  const server = meta?.server ?? 'a server';
  const when = meta ? ` @ ${clockOf(meta.atLocal)}` : '';
  return (
    <div
      className={`mt-1 inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 border cursor-help ${
        linked ? 'bg-coral-light text-coral border-coral/40 ring-2 ring-coral ring-offset-1' : 'bg-gold-light text-gold border-gold/30'
      }`}
      data-link-id={interactionId}
      data-testid={`pit-attribution-${interactionId}`}
      onMouseEnter={() => setHovered(interactionId)}
      onMouseLeave={() => setHovered(null)}
      title={explanation ?? `Attributed via ${matchRule}`}
    >
      ↳ attributed to {server}&rsquo;s {matchRule}{when}
    </div>
  );
};

// ---------- timeline grid ----------

const TimelineGrid: React.FC<{ data: PitTimelineResponse }> = ({ data }) => {
  const [hovered, setHovered] = React.useState<string | null>(null);
  const { columns, orders, window: win } = data;

  // positional per-server series (server index → series-N), reusing PosInspector palette.
  const seriesByServerId = new Map<string, number>(columns.map((c, i) => [c.server_id, (i % 5) + 1]));
  // interaction id → {server, at_local} for the order column's attribution metadata.
  const interactionMeta = new Map<string, InteractionMeta>();
  columns.forEach((c) => c.interactions.forEach((i) => interactionMeta.set(i.id, { server: serverLabel(c), atLocal: i.at_local })));

  return (
    <Hover.Provider value={{ hovered, setHovered }}>
      <div className="bg-white border border-softBorder rounded-2xl shadow-sm overflow-x-auto" data-testid="pit-timeline-grid">
        <div className="flex min-w-max">
          <TimeAxis duration={win.duration_seconds} tOffset={win.t_offset_seconds} tLocal={win.t_local} />
          {columns.length === 0 ? (
            <div className="flex items-center justify-center px-10 border-r border-softBorder" style={{ minWidth: 320, height: AXIS_HEIGHT }} data-testid="pit-no-columns">
              <p className="text-sm text-inkMuted text-center">No recordings active in this window.</p>
            </div>
          ) : (
            columns.map((c) => (
              <ServerColumn key={c.shift_id} column={c} series={seriesByServerId.get(c.server_id) ?? 1} duration={win.duration_seconds} tOffset={win.t_offset_seconds} />
            ))
          )}
          <OrderColumn orders={orders} duration={win.duration_seconds} tOffset={win.t_offset_seconds} interactionMeta={interactionMeta} />
        </div>
      </div>
    </Hover.Provider>
  );
};

// ---------- page ----------

const PitTimeline: React.FC = () => {
  const [mode, setMode] = React.useState<'time' | 'order'>('time');
  const [locationId, setLocationId] = React.useState('');
  const [atLocal, setAtLocal] = React.useState(''); // datetime-local value (YYYY-MM-DDTHH:mm)
  const [source, setSource] = React.useState('square');
  const [vendorOrderId, setVendorOrderId] = React.useState('');

  const [data, setData] = React.useState<PitTimelineResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = async () => {
    setError(null);
    if (mode === 'time') {
      if (!locationId) {
        setError('Select a location first.');
        return;
      }
      if (!atLocal) {
        setError('Pick a date and time (interpreted as the restaurant’s local time).');
        return;
      }
    } else if (!vendorOrderId.trim()) {
      setError('Enter a vendor order id.');
      return;
    }
    setLoading(true);
    setData(null);
    try {
      // datetime-local yields "YYYY-MM-DDTHH:mm" — backend wants seconds. Pure string op, no Date.
      const normalized = atLocal.length === 16 ? `${atLocal}:00` : atLocal;
      const resp = await fetchPitTimeline(
        mode === 'time'
          ? { mode: 'time', locationId, atLocal: normalized }
          : { mode: 'order', source: source.trim(), vendorOrderId: vendorOrderId.trim() },
      );
      setData(resp);
    } catch (e) {
      if (e instanceof PitNotFoundError) {
        setError(mode === 'order' ? `Order not found or not current: ${e.message}` : e.message);
      } else if (e instanceof PitValidationError) {
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load the timeline.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="pit-timeline">
      <ScorecardNavigation />
      <div className="max-w-[1600px] mx-auto p-4 md:p-8">
        <header className="mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-3xl font-display font-semibold text-ink">Point-in-Time Timeline</h1>
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-coral bg-coral-light/50 border border-coral/40 rounded-full px-2 py-0.5"
              data-testid="pit-internal-badge"
            >
              🔒 Bespoke internal · admin only
            </span>
          </div>
          <p className="text-sm text-inkMuted">
            See what every recording employee was saying, what the AI detected, and what the customer bought — all on one shared
            restaurant-local clock for a 6-minute window <span className="font-mono">[T−5m, T+1m]</span>.
          </p>
        </header>

        {/* ----- search ----- */}
        <section className="bg-white border border-softBorder rounded-xl p-5 shadow-sm mb-6" data-testid="pit-search">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setMode('time')}
              className={`px-3 py-1.5 rounded text-sm font-medium border ${mode === 'time' ? 'bg-coral text-white border-coral' : 'border-softBorder text-ink'}`}
              data-testid="pit-mode-time"
            >
              By location + time
            </button>
            <button
              onClick={() => setMode('order')}
              className={`px-3 py-1.5 rounded text-sm font-medium border ${mode === 'order' ? 'bg-coral text-white border-coral' : 'border-softBorder text-ink'}`}
              data-testid="pit-mode-order"
            >
              By POS order id
            </button>
          </div>

          {mode === 'time' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block text-inkMuted mb-1">Location (all tenants)</span>
                <LocationPicker value={locationId} onChange={setLocationId} />
              </label>
              <label className="text-sm">
                <span className="block text-inkMuted mb-1">Date &amp; time (restaurant-local)</span>
                <input
                  type="datetime-local"
                  step={1}
                  value={atLocal}
                  onChange={(e) => setAtLocal(e.target.value)}
                  className="w-full border border-softBorder rounded px-2 py-1.5"
                  data-testid="pit-datetime"
                />
              </label>
              <div className="flex items-end">
                <button
                  onClick={run}
                  disabled={loading}
                  className="w-full bg-coral text-white rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                  data-testid="pit-load"
                >
                  {loading ? 'Loading…' : 'Show timeline'}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block text-inkMuted mb-1">Source</span>
                <input
                  className="w-full border border-softBorder rounded px-2 py-1.5"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="square"
                  data-testid="pit-source"
                />
              </label>
              <label className="text-sm">
                <span className="block text-inkMuted mb-1">Vendor order id</span>
                <input
                  className="w-full border border-softBorder rounded px-2 py-1.5"
                  value={vendorOrderId}
                  onChange={(e) => setVendorOrderId(e.target.value)}
                  placeholder="order id — location &amp; T derived"
                  data-testid="pit-vendor-id"
                />
              </label>
              <div className="flex items-end">
                <button
                  onClick={run}
                  disabled={loading}
                  className="w-full bg-coral text-white rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                  data-testid="pit-load-order"
                >
                  {loading ? 'Loading…' : 'Show timeline'}
                </button>
              </div>
            </div>
          )}
          {error && <p className="mt-3 text-sm text-danger-text" data-testid="pit-error">{error}</p>}
        </section>

        {/* ----- result header ----- */}
        {data && (
            <section className="bg-white border border-softBorder rounded-xl p-4 shadow-sm mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="pit-result-header">
              <div>
                <span className="text-inkMuted">Location </span>
                <span className="font-semibold text-ink">{data.location.name}</span>
                <span className="text-inkMuted"> ({data.location.timezone})</span>
              </div>
              <div className="tabular-nums">
                <span className="text-inkMuted">Window </span>
                <span className="text-ink">{clockOf(data.window.start_local)} → {clockOf(data.window.end_local)}</span>
                <span className="text-coral font-semibold"> · T={clockOf(data.window.t_local)}</span>
              </div>
              <div data-testid="pit-column-count">
                <span className="font-semibold text-ink">{data.columns.length}</span>
                <span className="text-inkMuted"> server{data.columns.length === 1 ? '' : 's'} recording in this window</span>
              </div>
              {data.anchor.source === 'order_id' && data.anchor.matched_order && (
                <div className="flex items-center gap-1">
                  <span className="text-inkMuted">Anchor order</span>
                  <span className="font-mono text-xs text-ink">{data.anchor.matched_order.vendor_order_id}</span>
                  {data.anchor.matched_order.is_canceled && <Chip tone="warning">canceled</Chip>}
                </div>
              )}
            </section>
        )}

        {!data && !loading && !error && (
          <div className="bg-white border border-dashed border-softBorder rounded-2xl p-10 text-center text-sm text-inkMuted" data-testid="pit-empty">
            Pick a location and time, or look up a POS order id, to render the point-in-time timeline.
          </div>
        )}
      </div>

      {/* ----- full-bleed timeline: the grid spans the viewport so all server columns + the
           order column fit without clipping the receipt; busy locations still scroll-x (OQ4). ----- */}
      {data && (
        <div className="w-full px-4 pb-8" data-testid="pit-timeline-fullbleed">
          <TimelineGrid data={data} />
        </div>
      )}
    </div>
  );
};

export default PitTimeline;
